/*
 * Copyright 2025 Mia srl
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useCallback, useEffect } from 'react';
import analyticsService from '../services/analyticsService';

/**
 * Hook for tracking analytics events
 * @returns {Object} Analytics tracking functions
 */
export const useAnalytics = () => {
  const trackEvent = useCallback((eventType, properties = {}) => {
    analyticsService.trackEvent(eventType, properties);
  }, []);

  const trackClick = useCallback((buttonName, properties = {}) => {
    analyticsService.trackClick(buttonName, properties);
  }, []);

  const trackPageView = useCallback((pageName, properties = {}) => {
    analyticsService.trackPageView(pageName, properties);
  }, []);

  const trackConversation = useCallback((action, properties = {}) => {
    analyticsService.trackConversation(action, properties);
  }, []);

  const trackMessage = useCallback((action, properties = {}) => {
    analyticsService.trackMessage(action, properties);
  }, []);

  const trackToolCall = useCallback((toolName, properties = {}) => {
    analyticsService.trackToolCall(toolName, properties);
  }, []);

  const trackError = useCallback((errorType, properties = {}) => {
    analyticsService.trackError(errorType, properties);
  }, []);

  const trackFeature = useCallback((featureName, properties = {}) => {
    analyticsService.trackFeature(featureName, properties);
  }, []);

  const trackAuth = useCallback((action, properties = {}) => {
    analyticsService.trackAuth(action, properties);
  }, []);

  const trackNavigation = useCallback((from, to, properties = {}) => {
    analyticsService.trackNavigation(from, to, properties);
  }, []);

  const trackSearch = useCallback((query, properties = {}) => {
    analyticsService.trackSearch(query, properties);
  }, []);

  const trackPerformance = useCallback((metricName, value, properties = {}) => {
    analyticsService.trackPerformance(metricName, value, properties);
  }, []);

  const trackProject = useCallback((action, properties = {}) => {
    analyticsService.trackProject(action, properties);
  }, []);

  const trackLanguage = useCallback((language, properties = {}) => {
    analyticsService.trackLanguage(language, properties);
  }, []);

  const trackCodeGeneration = useCallback((language, properties = {}) => {
    analyticsService.trackCodeGeneration(language, properties);
  }, []);

  const trackAction = useCallback((category, action, properties = {}) => {
    analyticsService.trackAction(category, action, properties);
  }, []);

  const trackFile = useCallback((operation, properties = {}) => {
    analyticsService.trackFile(operation, properties);
  }, []);

  const trackAIInteraction = useCallback((interactionType, properties = {}) => {
    analyticsService.trackAIInteraction(interactionType, properties);
  }, []);

  const trackTemplate = useCallback((templateId, properties = {}) => {
    analyticsService.trackTemplate(templateId, properties);
  }, []);

  const trackDeployment = useCallback((action, properties = {}) => {
    analyticsService.trackDeployment(action, properties);
  }, []);

  const trackIntegration = useCallback((integration, action, properties = {}) => {
    analyticsService.trackIntegration(integration, action, properties);
  }, []);

  return {
    trackEvent,
    trackClick,
    trackPageView,
    trackConversation,
    trackMessage,
    trackToolCall,
    trackError,
    trackFeature,
    trackAuth,
    trackNavigation,
    trackSearch,
    trackPerformance,
    trackProject,
    trackLanguage,
    trackCodeGeneration,
    trackAction,
    trackFile,
    trackAIInteraction,
    trackTemplate,
    trackDeployment,
    trackIntegration,
  };
};

/**
 * Hook to track page view on mount
 * @param {string} pageName - Name of the page
 * @param {Object} properties - Additional properties
 */
export const usePageView = (pageName, properties = {}) => {
  useEffect(() => {
    analyticsService.trackPageView(pageName, properties);
  }, [pageName, properties]);
};

/**
 * Hook to track feature usage
 * @param {string} featureName - Name of the feature
 * @returns {Function} Function to track feature usage
 */
export const useFeatureTracking = (featureName) => {
  return useCallback((properties = {}) => {
    analyticsService.trackFeature(featureName, properties);
  }, [featureName]);
};

/**
 * Hook to track button clicks with analytics
 * @param {string} buttonName - Name of the button
 * @returns {Function} Function to call on button click
 */
export const useClickTracking = (buttonName) => {
  return useCallback((properties = {}) => {
    analyticsService.trackClick(buttonName, properties);
  }, [buttonName]);
};

/**
 * Hook to track errors
 * @returns {Function} Function to track errors
 */
export const useErrorTracking = () => {
  return useCallback((error, context = {}) => {
    analyticsService.trackError(error.name || 'Error', {
      message: error.message,
      stack: error.stack,
      ...context,
    });
  }, []);
};

/**
 * Hook to track performance metrics
 * @param {string} metricName - Name of the metric
 * @returns {Object} Performance tracking utilities
 */
export const usePerformanceTracking = (metricName) => {
  const startTime = useCallback(() => {
    return performance.now();
  }, []);

  const endTime = useCallback((start, properties = {}) => {
    const duration = performance.now() - start;
    analyticsService.trackPerformance(metricName, duration, properties);
    return duration;
  }, [metricName]);

  return { startTime, endTime };
};

export default useAnalytics;
